import React from 'react';
import { View } from 'react-native';
import { styled } from 'dripsy';
import { observer } from 'mobx-react-lite';

import { BaseText } from '../common/BaseText';
import { Separator } from '../common/Separator';
import { IconTouchable } from '../common/IconTouchable';
import type { Message } from '@models/Chats/MessageModel';
import { RichText } from '../common/RichText';

interface IMessageStyleProps {
  isIncome: boolean;
}

const RowContainer = styled(View)((props: IMessageStyleProps) => ({
  alignItems: 'center',
  flexDirection: props.isIncome ? 'row' : 'row-reverse',
}));

const MessageContainer = styled(View)(
  (props: IMessageStyleProps) => ({
    flexDirection: 'row',
    alignItems: 'center',
    padding: '$6',
    flexShrink: 1,
    borderRadius: 12,
    borderTopLeftRadius: props.isIncome ? 0 : 12,
    borderTopRightRadius: props.isIncome ? 12 : 0,
    backgroundColor: props.isIncome ? '$background30' : '$primary300',
  }),
);

const MessageText = styled(RichText)({
  variant: 'text.medium',
  color: '$heading800',
});

const TimeContainer = styled(View)({
  flexGrow: 1,
});

const TimeText = styled(BaseText)((props: IMessageStyleProps) => ({
  variant: 'text.small',
  color: '$text70',
  textAlign: props.isIncome ? 'left' : 'right',
}));

const RetryIcon = styled(IconTouchable)({
  ml: '$2',
});

export interface IMessageItemProps {
  item: Message;
}

export const MessageItem = observer(({ item }: IMessageItemProps) => {
  async function retrySendMessage() {
    try {
      await item.retry.run();
    } catch (error) {
      // Do nothing
    }
  }

  const retryIconName = item.retry.inProgress
    ? 'time-circle-light'
    : 'info-circle-light';

  const retryIconColor = item.retry.inProgress
    ? '$heading600'
    : '$error200';

  return (
    <RowContainer isIncome={item.isIncome}>
      <MessageContainer isIncome={item.isIncome}>
        <MessageText>{item.message}</MessageText>

        {!!item.hasError && (
          <RetryIcon
            disabled={item.retry.inProgress}
            name={retryIconName}
            onPress={retrySendMessage}
            color={retryIconColor}
            size={22}
          />
        )}
      </MessageContainer>

      <Separator size="$8" vertical />
      <TimeContainer>
        <TimeText isIncome={item.isIncome}>
          {item.formattedTime}
        </TimeText>
      </TimeContainer>
    </RowContainer>
  );
});
